// src/pages/admin/Imports.tsx
//
// FileMaker imports pane — 2-phase additive delta import with
// snapshot/rollback. Pick a batch directory from the drop zone, click
// "Preview" to see the per-table new/updated/skipped/FK-broken
// report, then "Apply" to apply. Every apply takes a snapshot first;
// the snapshot list offers per-batch rollback.
//
// The destructive /api/imports/run endpoint still exists (admin only)
// for the historical full-refresh use case but is no longer the
// default — see the README.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Database,
  Eye,
  Play,
  CheckCircle2,
  XCircle,
  History,
  Undo2,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { authHeaders } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";

interface BatchRow {
  batch_id: string;
  source_dir: string;
  started_at: string | null;
  finished_at: string | null;
  status: string;
}

interface SampleEntry {
  key?: unknown;
  row?: Record<string, unknown>;
  diff?: Record<string, [unknown, unknown]>;
  reason?: string;
}

interface TableReport {
  status: string;
  reason?: string;
  new: number;
  updated: number;
  skipped_no_data: number;
  fk_broken: number;
  errors: number;
  new_samples: SampleEntry[];
  updated_samples: SampleEntry[];
  fk_broken_samples: SampleEntry[];
}

interface PreviewReport {
  batch_dir: string;
  generated_at: string;
  files_scanned: { filename: string; size_bytes: number }[];
  tables: Record<string, TableReport>;
  totals: { new: number; updated: number; skipped_no_data: number;
            fk_broken: number; errors: number };
}

interface ApplyReport {
  batch_id: string;
  snapshot_id: string;
  tables: Record<string, { applied: number; status: string; error?: string }>;
  errors: number;
}

interface SnapshotRow {
  snapshot_id: string;
  batch_id: string;
  created_at: string;
  actor: string;
  tables_touched: Record<string, number>;
}

function badge(status: string, label = status) {
  if (status === "passed" || status === "applied" || status === "ok")
    return <Badge className="bg-accent-green/15 text-accent-green">{label}</Badge>;
  if (status === "running" || status === "preview" || status === "applied_partial" || status === "interrupted")
    return <Badge className="bg-accent-amber/15 text-accent-amber">{label}</Badge>;
  if (status === "FAILED" || status === "failed")
    return <Badge className="bg-accent-rose/15 text-accent-rose">{label}</Badge>;
  return <Badge>{label}</Badge>;
}

function fmtKey(k: unknown): string {
  if (Array.isArray(k)) return k.join(" / ");
  if (k === null || k === undefined) return "—";
  return String(k);
}

function SampleList({ samples, kind }: { samples: SampleEntry[]; kind: "new" | "updated" | "fk" }) {
  const { t } = useTranslation();
  if (samples.length === 0) return <p className="text-fg-quaternary">{t("admin.imports.noSamples")}</p>;
  return (
    <ul className="space-y-1 text-xs">
      {samples.map((s, i) => (
        <li key={i} className="rounded border border-border bg-bg-canvas/40 p-2">
          {kind === "updated" && s.key !== undefined && s.diff ? (
            <>
              <code className="text-fg-tertiary">{fmtKey(s.key)}</code>
              <div className="mt-1 space-y-0.5">
                {Object.entries(s.diff).map(([col, [old, ne]]) => (
                  <div key={col} className="font-mono text-[11px]">
                    <span className="text-fg-tertiary">{col}:</span>{" "}
                    <span className="text-accent-rose line-through">{String(old)}</span>
                    {" → "}
                    <span className="text-accent-green">{String(ne)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : kind === "fk" && s.row ? (
            <>
              <span className="text-fg-tertiary">{s.reason ?? t("admin.imports.fkBroken")}</span>
              <pre className="mt-1 overflow-x-auto text-[10px] text-fg-secondary">
                {JSON.stringify(s.row)}
              </pre>
            </>
          ) : (
            <>
              {s.key !== undefined && (
                <code className="text-fg-tertiary">{fmtKey(s.key)}</code>
              )}
              {s.row && (
                <pre className="mt-1 overflow-x-auto text-[10px] text-fg-secondary">
                  {JSON.stringify(s.row)}
                </pre>
              )}
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function TableCard({ name, report }: { name: string; report: TableReport }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <CardTitle className="flex items-center gap-2 text-sm">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <code className="font-mono">{name}</code>
          {badge(report.status === "ok" ? "ok" : report.status === "skipped_no_data" ? "default" : report.status)}
          <span className="ml-auto flex gap-2 text-[11px] text-fg-tertiary">
            <span className="text-accent-green">+{report.new}</span>
            <span className="text-accent-amber">~{report.updated}</span>
            {report.fk_broken > 0 && <span className="text-accent-rose">FK {report.fk_broken}</span>}
            {report.skipped_no_data > 0 && <span className="text-fg-quaternary">{t("admin.imports.skipped")} {report.skipped_no_data}</span>}
          </span>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">
              {t("admin.imports.newSamples", { count: report.new })}
            </h5>
            <SampleList samples={report.new_samples} kind="new" />
          </div>
          <div>
            <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">
              {t("admin.imports.changedSamples", { count: report.updated })}
            </h5>
            <SampleList samples={report.updated_samples} kind="updated" />
          </div>
          <div>
            <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">
              {t("admin.imports.fkBrokenSamples", { count: report.fk_broken })}
            </h5>
            <SampleList samples={report.fk_broken_samples} kind="fk" />
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function AdminImports() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [available, setAvailable] = useState<string[]>([]);
  const [selected, setSelected] = useState("");
  const [running, setRunning] = useState<"" | "preview" | "apply" | "rollback">("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<PreviewReport | null>(null);
  const [lastApply, setLastApply] = useState<ApplyReport | null>(null);
  const [snapshots, setSnapshots] = useState<SnapshotRow[]>([]);
  // Per-operation progress (0-100) so the page shows its own bar
  // for ops started in this tab. The header chip is the global
  // view; this one is local so the user sees the action they
  // started is still in flight even after they navigate.
  const [progress, setProgress] = useState<{pct: number; msg: string}>({pct: 0, msg: ""});

  async function refresh() {
    const r = await fetch("/api/imports", { headers: authHeaders() });
    const j = await r.json();
    setBatches(j.batches);
    setAvailable(j.available_dirs);
    if (!selected && j.available_dirs.length > 0) {
      setSelected(j.available_dirs[j.available_dirs.length - 1]);
    }
    const sr = await fetch("/api/imports/snapshots", { headers: authHeaders() });
    if (sr.ok) {
      const sj = await sr.json();
      setSnapshots(sj.snapshots);
    }
  }

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) {
      refresh().catch((e) => setError(String(e)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isReady, auth.isAuthenticated]);

  // Start a job, then poll its progress + fetch the result when done.
  async function startJob(
    path: string, body: Record<string, unknown>,
  ): Promise<unknown> {
    setError("");
    setProgress({pct: 0, msg: t("admin.imports.starting")});
    const start = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body),
    });
    const sj = await start.json();
    if (!start.ok) throw new Error(sj.detail ?? t("admin.imports.httpStatus", { status: start.status }));
    const jobId = sj.job_id as string;
    // First fetch (before the loop) so TS can narrow the type.
    const r0 = await fetch(`/api/imports/jobs/${jobId}`, {
      headers: authHeaders(),
    });
    if (!r0.ok) throw new Error(t("admin.imports.jobNotFound", { id: jobId }));
    let last: { phase: string; progress: number; message: string;
                error: string | null } = await r0.json();
    setProgress({pct: last.progress, msg: last.message});
    while (last.phase === "running") {
      await new Promise((res) => setTimeout(res, 400));
      const r = await fetch(`/api/imports/jobs/${jobId}`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(t("admin.imports.jobNotFound", { id: jobId }));
      last = await r.json();
      setProgress({pct: last.progress, msg: last.message});
    }
    if (last.phase === "error") {
      throw new Error(last.error ?? t("admin.imports.jobFailed"));
    }
    const rr = await fetch(`/api/imports/jobs/${jobId}/result`, {
      headers: authHeaders(),
    });
    if (!rr.ok) throw new Error(t("admin.imports.resultFetchFailed", { status: rr.status }));
    return rr.json();
  }

  async function doPreview() {
    setRunning("preview");
    try {
      const result = await startJob(
        "/api/imports/preview", { batch_dir: selected },
      ) as PreviewReport;
      setPreview(result);
    } catch (e) { setError(String(e)); }
    finally { setRunning(""); setProgress({pct: 0, msg: ""}); }
  }

  async function doApply() {
    if (!preview) return;
    setRunning("apply");
    try {
      const result = await startJob(
        "/api/imports/approve",
        {
          batch_dir: selected,
          batch_id: preview.batch_dir.split("/").pop() || preview.batch_dir,
          expected_totals: preview.totals,
        },
      ) as ApplyReport;
      setLastApply(result);
      setPreview(null);
      await refresh();
    } catch (e) { setError(String(e)); }
    finally { setRunning(""); setProgress({pct: 0, msg: ""}); }
  }

  async function doRollback(snapId: string) {
    if (!window.confirm(
      t("admin.imports.rollBackConfirm", { id: snapId }))) return;
    setRunning("rollback");
    try {
      const r = await fetch("/api/imports/rollback", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ snapshot_id: snapId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail ?? t("admin.imports.httpStatus", { status: r.status }));
      setLastApply(null);
      await refresh();
    } catch (e) { setError(String(e)); }
    finally { setRunning(""); }
  }

  async function showReport(batchId: string) {
    const r = await fetch(`/api/imports/${batchId}`, { headers: authHeaders() });
    const j = await r.json();
    if (j.report) setLastApply(j.report);
  }

  const totals = preview?.totals;
  const safeToApply = preview
    && preview.totals.fk_broken === 0
    && preview.totals.errors === 0;
  const opInFlight = running !== "";

  return (
    <div className="space-y-4">
      {opInFlight && (
        <Card className="border-accent-indigo/30 bg-accent-indigo/[0.04]">
          <CardContent className="flex items-center gap-3 py-3">
            <Loader2 className="h-4 w-4 animate-spin text-accent-indigo" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-510 text-fg-primary">
                {running === "preview" && t("admin.imports.previewingDelta")}
                {running === "apply" && t("admin.imports.applyingDelta")}
                {running === "rollback" && t("admin.imports.rollingBack")}
              </p>
              <p className="text-[10px] text-fg-tertiary">{progress.msg}</p>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded bg-bg-canvas">
                <div className="h-full bg-accent-indigo transition-all duration-300"
                     style={{ width: `${Math.max(2, progress.pct)}%` }} />
              </div>
            </div>
            <span className="font-mono text-xs tabular-nums text-accent-indigo">
              {progress.pct}%
            </span>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" /> {t("admin.imports.title")}
          </CardTitle>
          <CardDescription>
            {t("admin.imports.descBefore")} <code>data/filemaker-export/incoming/YYYY-MM-DD/</code>,
            {t("admin.imports.descAfter")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border border-border bg-bg-panel px-3 py-2 text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {available.length === 0 && <option value="">{t("admin.imports.noBatchDirs")}</option>}
              {available.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <Button
              variant="secondary"
              onClick={doPreview}
              disabled={!selected || running !== ""}
            >
              <Eye className="h-4 w-4" />
              {running === "preview" ? t("admin.imports.previewing") : t("admin.imports.previewDelta")}
            </Button>
            {preview && (
              <Button
                onClick={doApply}
                disabled={!safeToApply || running !== ""}
                title={safeToApply
                  ? t("admin.imports.applyTitle")
                  : t("admin.imports.applyBlockedTitle")}
              >
                <Play className="h-4 w-4" />
                {running === "apply" ? t("admin.imports.applying") : t("admin.imports.approveApply")}
              </Button>
            )}
          </div>
          {error && (
            <div className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {preview && totals && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" /> {t("admin.imports.previewReport")}
              <span className="ml-auto text-xs text-fg-tertiary">
                {preview.generated_at.slice(0, 19).replace("T", " ")}Z
              </span>
            </CardTitle>
            <CardDescription>
              {t("admin.imports.addOrUpdate")} <strong className="text-accent-green">+{totals.new}</strong>{" "}
              {t("admin.imports.changedLabel")} <strong className="text-accent-amber">~{totals.updated}</strong>{" "}
              {t("admin.imports.fkBrokenLabel")} <strong className={totals.fk_broken > 0 ? "text-accent-rose" : ""}>
                {totals.fk_broken}
              </strong>{" "}
              {t("admin.imports.skippedLabel")} {totals.skipped_no_data}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {preview.files_scanned.length > 0 && (
              <div className="rounded border border-border bg-bg-canvas/40 p-3 text-xs text-fg-tertiary">
                <strong>{t("admin.imports.files")}</strong>{" "}
                {preview.files_scanned.map((f) =>
                  `${f.filename} (${(f.size_bytes / 1024).toFixed(1)} kB)`,
                ).join(" · ")}
              </div>
            )}
            <div className="space-y-2">
              {Object.entries(preview.tables).map(([name, report]) => (
                <TableCard key={name} name={name} report={report} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {lastApply && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {lastApply.errors === 0
                ? <CheckCircle2 className="h-5 w-5 text-accent-green" />
                : <XCircle className="h-5 w-5 text-accent-rose" />}
              {t("admin.imports.applyReportTitle")} {lastApply.batch_id}
            </CardTitle>
            <CardDescription>
              {t("admin.imports.snapshotLabel")} <code>{lastApply.snapshot_id}</code> ·{" "}
              {t("admin.imports.rowsCount", { count: Object.values(lastApply.tables).reduce(
                (s, row) => s + (row.applied ?? 0), 0) })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(lastApply.tables).map(([name, t]) => (
                  <tr key={name} className="border-b border-border/50">
                    <td className="py-1 font-mono text-xs">{name}</td>
                    <td className="text-right">{t.applied}</td>
                    <td className="w-20 text-right">{badge(t.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" /> {t("admin.imports.snapshotsTitle")}
          </CardTitle>
          <CardDescription>
            {t("admin.imports.snapshotsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-sm text-fg-tertiary">{t("admin.imports.noSnapshots")}</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-fg-tertiary">
                  <th className="py-1">{t("admin.imports.snapshotCol")}</th>
                  <th>{t("admin.imports.batchCol")}</th>
                  <th>{t("admin.imports.createdCol")}</th>
                  <th>{t("admin.imports.byCol")}</th>
                  <th>{t("admin.imports.rowsTouchedCol")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => {
                  const touched = Object.values(s.tables_touched || {})
                    .reduce((a, b) => a + b, 0);
                  return (
                    <tr key={s.snapshot_id} className="border-b border-border/50">
                      <td className="py-1 font-mono text-xs">{s.snapshot_id}</td>
                      <td>{s.batch_id}</td>
                      <td className="text-xs">{s.created_at?.slice(0, 19).replace("T", " ")}</td>
                      <td className="text-xs">{s.actor}</td>
                      <td className="text-xs">{touched}</td>
                      <td className="text-right">
                        <Button variant="ghost" size="sm"
                          onClick={() => doRollback(s.snapshot_id)}
                          disabled={running !== ""}>
                          <Undo2 className="h-3.5 w-3.5" />
                          {t("admin.imports.rollBack")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-4 w-4" /> {t("admin.imports.batchHistory")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-fg-tertiary">
                <th className="py-1">{t("admin.imports.batchCol")}</th>
                <th>{t("admin.imports.startedCol")}</th>
                <th>{t("admin.imports.finishedCol")}</th>
                <th>{t("admin.imports.statusCol")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id} className="border-b border-border/50">
                  <td className="py-1 font-mono text-xs">{b.batch_id}</td>
                  <td className="text-xs">{b.started_at?.slice(0, 19).replace("T", " ") ?? "—"}</td>
                  <td className="text-xs">{b.finished_at?.slice(0, 19).replace("T", " ") ?? "—"}</td>
                  <td>{badge(b.status)}</td>
                  <td className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => showReport(b.batch_id)}>
                      <FileSpreadsheet className="h-3.5 w-3.5" /> {t("admin.imports.report")}
                    </Button>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr><td colSpan={5} className="py-4 text-center text-fg-tertiary">{t("admin.imports.noImports")}</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
